<template>
  <div class="bg-[#ececec]">
    <!-- Breadcrumbs -->
    <BaseContainer>
      <V3Breadcrumbs
        :breadcrumb-items="[
          { type: 'link', text: $t('products.home'), to: '/' } as const,
          {
            type: 'link',
            text: $t('pages.blogs.allBlogs'),
            to: '/blogs'
          } as const
        ]"
        :current-page-text="blogDetailData?.display_title || ''"
      />
    </BaseContainer>

    <!-- Banner -->
    <section
      v-if="!!blogDetailData?.banner && !blogDetailData?.hide_banner"
      :key="blogDetailData?.banner"
      :class="'relative'"
    >
      <V2FileRenderer
        custom-class="w-full h-full left-0 top-0 object-cover object-center"
        :image-url="blogDetailData?.banner ?? ''"
      />

      <BaseContainer>
        <h1
          class="text-center text-[32px] md:text-[64px] lg:text-[80px] md:leading-[80px] py-5 md:py-10 font-bold"
        >
          {{ blogDetailData?.display_title }}
        </h1>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="text-center text-[18px] md:text-[24px]"
          v-html="formattedSubtitle(blogDetailData?.display_subtitle)"
        />
        <!--eslint-enable-->
      </BaseContainer>
      <div class="border-border-divider border-b pt-[20px] md:pt-[30px]" />
    </section>

    <BaseContainer v-if="blogDetailData">
      <Suspense>
        <V2BlogDetail :blog-detail-data="blogDetailData" />
      </Suspense>
    </BaseContainer>

    <V2CaseClubBlog show-navigate-desktop />

    <section class="bg-white py-[30px]">
      <V2ProductListSwiper
        v-if="hotPicks.length > 0"
        :title="hotPicksTitle"
        slide-key="product-style-a"
        :data="hotPicks"
        :data-row2="hotPicksRow2"
        recommendation
        style
      />
    </section>

    <CaseClubSocial hide-title show-navigate-desktop />
  </div>
</template>

<script setup lang="ts">
import { BlogRepository } from '~/repository/v2/blogRepository'
import { cmsRepository } from '~/repository/cmsRepository'
import type { GetBlogDetailResponse } from '~/types/apis/v2/getBlogDetail'

// Nuxt tools
const { $serverApi } = useNuxtApp()
const config = useRuntimeConfig()
const { locale } = useI18n()
const route = useRoute()
const { t } = useI18n()
const localePath = useLocalePath()

// Slug and params
const blogslug = computed(() => route.params.blogSlug as string)

// ─── Slug redirects ───────────────────────────────────────────────
const BLOG_SLUG_REDIRECTS: Record<string, string> = {
  'power-bank-flight-rules-2568': 'power-bank-flight-rules',
  'power-bank-flight-rules-2569': 'power-bank-flight-rules'
}
const redirectTarget = BLOG_SLUG_REDIRECTS[blogslug.value]
if (redirectTarget) {
  await navigateTo(localePath(`/blog/${redirectTarget}`), { redirectCode: 301, replace: true })
}

// Blog Repository
const blogRepo = BlogRepository($serverApi)
const cmsRepo = cmsRepository($serverApi)

// SSR-ready fetch blog detail
const { data: blogDetailData } = await useAsyncData<
  GetBlogDetailResponse['data'] | null
>(
  'getBlogDetail',
  async () => {
    const response = await blogRepo.getBlogDetail(blogslug.value, locale.value)

    if (response.data) {
      const media = response.data.blog_media_block_data
      if (typeof media === 'string') {
        response.data.blog_media_block_data = JSON.parse(media || '[]')
      }

      return response.data
    }

    return null
  },
  {
    server: true,
    lazy: false,
    default: () => null
  }
)

const getArticleBody = () => {
  const mediaBlockData = blogDetailData.value?.blog_media_block_data
  if (!mediaBlockData) return null

  let textBody = ''
  for (const media of mediaBlockData || []) {
    const sectionBlockData = media?.blog_section_block_data
    if (!sectionBlockData) continue
    for (const section of sectionBlockData) {
      textBody = textBody.concat(section.display_description)
    }
  }
  // Remove html tag
  return textBody.replace(/<\/?[^>]+(>|$)/g, '')
}

const getWordCount = () => {
  return getArticleBody()?.length
}

// Set SEO meta
useSeoMeta({
  title: () =>
    blogDetailData.value?.meta_title || t('pages.blog.meta.defaultTitle'),
  description: () =>
    blogDetailData.value?.meta_description ||
    t('pages.blog.meta.defaultDescription'),
  ogImage: () =>
    blogDetailData.value?.thumbnail
      ? config.public.mediaUrl + blogDetailData.value.thumbnail
      : undefined,
  ogImageSecureUrl: () =>
    blogDetailData.value?.thumbnail
      ? config.public.mediaUrl + blogDetailData.value.thumbnail
      : undefined,
  keywords: 'blog-list'
})

useSchemaOrg([
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${config.public.siteUrl}`
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'All Blogs',
        item: `${config.public.siteUrl}/${locale.value}/blogs`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: blogDetailData.value?.display_title ?? '',
        item: `${config.public.siteUrl}/${locale.value}/blogs/${blogslug.value}`
      }
    ]
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: blogDetailData.value?.display_title,
    image: blogDetailData.value?.banner ?? '',
    datePublished: blogDetailData.value?.publish_datetime ?? '',
    dateModified: blogDetailData.value?.updated_at ?? '',
    publisher: {
      '@type': 'Organization',
      name: 'CaseClub Thailand'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${config.public.siteUrl}/${locale.value}/blogs`
    },
    articleBody: getArticleBody(),
    wordCount: getWordCount(),
    inLanguage: locale.value
  }
])

// Hot picks: move to client only
const hotPicks = ref<AlgoliaProductV2[]>([])
const hotPicksRow2 = ref<AlgoliaProductV2[]>([])
const hotPicksMeta = ref<any>({})
const hotPicksTitle = computed(() =>
  locale.value === 'th'
    ? hotPicksMeta.value?.header_th || t('landing.titleHotProduct')
    : hotPicksMeta.value?.header_en || t('landing.titleHotProduct')
)

if (import.meta.client) {
  const fetchWidgetHotPicksByStyle = async () => {
    const response: any = await cmsRepo.getWidgetProducts(
      'collection-hot-picks-by-style'
    )
    hotPicks.value = response?.data?.groups?.[0]?.items ?? []
    hotPicksRow2.value = response?.data?.groups?.[1]?.items ?? []
    hotPicksMeta.value = response?.data?.meta ?? {}
  }

  onMounted(async () => {
    await fetchWidgetHotPicksByStyle()

    setTimeout(() => {
      if (blogDetailData.value && hotPicks.value.length > 0) {
        window.scrollTo(0, 0)
      }
    }, 100)
  })
}

// Format helper
function formattedSubtitle(text: string) {
  return text.replace(/\n/g, '<br>')
}
</script>
